import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import {
  yearFromPeriod,
  yearToPeriod,
} from "../../../components/dashboard/accounting/accountingYearPeriod";
import { AccountDetailDialog } from "../../../components/dashboard/accounting/AccountDetailDialog";
import {
  fetchAccountingResultReport,
  fetchAccountingImportStatus,
  downloadAccountingResultReportDebugExport,
} from "../../../api/accounting";
import { isLedgerSyncTruncated } from "./ledgerSyncStatus";
import { updateUrlWithException } from "../../../components/Utils";
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";

const financialColor = (value) =>
  signedFinancialColorClass(value, { neutralClass: "text-slate-950" });

const FETCH_DEBOUNCE_MS = 400;

const formatCurrency = (value) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("nb-NO", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

function readInitialYear() {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  return sp.get("year") ? parseInt(sp.get("year"), 10) : new Date().getFullYear();
}

export const AccountingResultReportPage = () => {
  const { t } = useTranslation();
  const [year, setYear] = useState(readInitialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncRequired, setSyncRequired] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetailOpen, setAccountDetailOpen] = useState(false);
  const [debugExportLoading, setDebugExportLoading] = useState(false);

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(async (selectedYear) => {
      setLoading(true);
      setError(null);
      setSyncRequired(false);
      try {
        const result = await fetchAccountingResultReport({ year: selectedYear });
        if (result.error) {
          setData(null);
          setSyncRequired(!!result.syncRequired);
          setError(result.data?.errors?.[0] || t("accounting.result_report.load_error"));
          if (result.syncRequired) {
            const statusRes = await fetchAccountingImportStatus();
            if (!statusRes.error) {
              setImportStatus(statusRes.data);
            }
          }
        } else {
          setData(result.data);
        }
      } catch {
        setError(t("accounting.result_report.load_error"));
      } finally {
        setLoading(false);
      }
    }, FETCH_DEBOUNCE_MS);
  }

  useEffect(() => {
    debouncedFetchRef.current(year);
  }, [year]);

  useEffect(() => {
    updateUrlWithException({ year });
  }, [year]);

  useEffect(() => {
    return () => debouncedFetchRef.current?.cancel();
  }, []);

  const period = useMemo(() => yearToPeriod(year), [year]);

  const handlePeriodChange = useCallback((nextPeriod) => {
    setYear(yearFromPeriod(nextPeriod));
  }, []);

  const totals = data?.totals || {};

  const handleAccountClick = useCallback((account) => {
    setSelectedAccount(account);
    setAccountDetailOpen(true);
  }, []);

  const handleAccountDetailClose = useCallback(() => {
    setAccountDetailOpen(false);
    setSelectedAccount(null);
  }, []);

  const handleDownloadDebugExport = useCallback(async () => {
    setDebugExportLoading(true);
    try {
      await downloadAccountingResultReportDebugExport({
        year,
        filename: `result-report-debug-${year}.json`,
      });
    } catch (err) {
      setError(err.message || t("accounting.result_report.debug_export.error"));
    } finally {
      setDebugExportLoading(false);
    }
  }, [year, t]);

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader title={t("accounting.menu.result_report")} />

      <div className="mx-auto max-w-7xl w-full px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <PeriodSelector
              periodType={period.periodType}
              from={period.from}
              to={period.to}
              allowedPeriodTypes={["year"]}
              onChange={handlePeriodChange}
            />
          </div>

          {data?.synced_at && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <ArrowPathIcon className="h-4 w-4 text-slate-400" />
              <span>
                {t("accounting.ledger.last_synced", {
                  date: formatDateTime(data.synced_at),
                })}
              </span>
              <span className="text-slate-400">·</span>
              <span>{t("accounting.ledger.source_local")}</span>
            </div>
          )}

          {isLedgerSyncTruncated(data) && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status">
              {t("accounting.ledger.vouchers_truncated")}
            </div>
          )}

          {syncRequired && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
              <p>{t("accounting.ledger.sync_required_hint")}</p>
              {importStatus?.ledger_sync?.created_at && (
                <p className="text-xs text-amber-800">
                  {t("accounting.ledger.last_sync_attempt", {
                    date: formatDateTime(importStatus.ledger_sync.created_at),
                  })}
                </p>
              )}
              <p className="text-xs text-amber-800">
                {t("accounting.ledger.sync_via_integrations")}
              </p>
            </div>
          )}

          {loading && !data && (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500">
              {t("accounting.result_report.loading")}
            </div>
          )}

          {error && !loading && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && data && (
            <>
              <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700">
                    <ChartBarIcon className="h-5 w-5 text-emerald-500" />
                    {t("accounting.result_report.total_income")}
                  </div>
                  <p className={`text-2xl font-semibold tabular-nums ${financialColor(totals.operating_income)}`}>
                    {formatCurrency(totals.operating_income)}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-700">
                    <ChartBarIcon className="h-5 w-5 text-rose-500" />
                    {t("accounting.result_report.total_costs")}
                  </div>
                  <p className={`text-2xl font-semibold tabular-nums ${financialColor(totals.total_operating_costs)}`}>
                    {formatCurrency(totals.total_operating_costs)}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-700">
                    <ChartBarIcon className="h-5 w-5 text-blue-500" />
                    {t("accounting.result_report.operating_result")}
                  </div>
                  <p className={`text-2xl font-semibold tabular-nums ${financialColor(totals.operating_result)}`}>
                    {formatCurrency(totals.operating_result)}
                  </p>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                  <div className="space-y-2">
                    <p>{data.methodology_note}</p>
                    <p>
                      <a
                        href="/docs/modules/ACCOUNTING_RESULT_REPORT.md"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-700 hover:text-blue-800"
                      >
                        {t("accounting.result_report.methodology_link")}
                      </a>
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 gap-3 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("accounting.result_report.table_title", { year })}
                  </h3>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleDownloadDebugExport}
                      disabled={debugExportLoading}
                      className="inline-flex items-center px-3 py-1.5 border border-amber-300 text-sm font-medium rounded-md text-amber-800 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-60"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      {debugExportLoading
                        ? t("accounting.result_report.debug_export.loading")
                        : t("accounting.result_report.debug_export.button")}
                    </button>
                    <DocumentTextIcon className="h-5 w-5 text-slate-400" />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="px-6 py-3">{t("accounting.result_report.account_number")}</th>
                        <th className="px-3 py-3">{t("accounting.result_report.account_name")}</th>
                        <th className="px-3 py-3 text-right">{t("accounting.result_report.amount")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(data.sections || []).length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-10 text-center text-gray-500">
                            {t("accounting.result_report.no_rows")}
                          </td>
                        </tr>
                      ) : (
                        (data.sections || []).map((section) => (
                          <React.Fragment key={section.key}>
                            <tr className="bg-slate-50">
                              <td colSpan={2} className="px-6 py-3 font-semibold text-slate-900">
                                {section.label}
                              </td>
                              <td className={`px-3 py-3 text-right font-semibold tabular-nums ${financialColor(section.subtotal)}`}>
                                {formatCurrency(section.subtotal)}
                              </td>
                            </tr>
                            {(section.accounts || []).map((account) => (
                              <tr
                                key={`${section.key}-${account.account_number}`}
                                className="cursor-pointer hover:bg-blue-50/60"
                                onClick={() => handleAccountClick(account)}
                                title={t("accounting.result_report.account_detail.click_hint")}
                              >
                                <td className="px-6 py-2 pl-10 tabular-nums text-slate-700">
                                  {account.account_number}
                                </td>
                                <td className="px-3 py-2 text-slate-700">{account.account_name}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${financialColor(account.amount)}`}>
                                  {formatCurrency(account.amount)}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      )}

                      {(data.sections || []).length > 0 && (
                        <>
                          <tr className="border-t-2 border-slate-200 bg-slate-100">
                            <td colSpan={2} className="px-6 py-3 font-semibold text-slate-900">
                              {t("accounting.result_report.operating_result")}
                            </td>
                            <td className={`px-3 py-3 text-right font-semibold tabular-nums ${financialColor(totals.operating_result)}`}>
                              {formatCurrency(totals.operating_result)}
                            </td>
                          </tr>
                          {(totals.financial_income > 0 || totals.financial_expenses > 0) && (
                            <tr className="bg-slate-50">
                              <td colSpan={2} className="px-6 py-3 font-semibold text-slate-900">
                                {t("accounting.result_report.result_before_tax")}
                              </td>
                              <td className={`px-3 py-3 text-right font-semibold tabular-nums ${financialColor(totals.result_before_tax)}`}>
                                {formatCurrency(totals.result_before_tax)}
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <AccountDetailDialog
        open={accountDetailOpen}
        onClose={handleAccountDetailClose}
        accountNumber={selectedAccount?.account_number}
        accountName={selectedAccount?.account_name}
        year={year}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default AccountingResultReportPage;
